import Konva from 'konva';
import {KonvaEventObject} from 'konva/types/Node';
import {Rect, Group, Text, Path} from 'react-konva';
import {KonvaPointerEvent} from 'konva/types/PointerEvents';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
  TAG_FILL,
  TAG_ICON,
  TEXT_LABEL,
  TEXT_COLOR,
  DELETE_ICON,
  SELECTED_FILL,
  BOX_LABEL_DEFAULT,
  TAG_SELECTED_FILL,
  TEXT_SELECTED_COLOR,
} from './const';

import {LabelProps, LabelPropsEvent} from './types';

const SPACER = 15;
const TAG_WIDTH = 20;
const CHAR_WIDTH = 10;
const LABEL_HEIGHT = 25;
const DELETE_ICON_WIDTH = 23;
export const LABEL_NAME = 'label';

type TagProps = {
  xBuffer?: number;
  yBuffer?: number;
};

export function Tag({
  // Reference object center
  // Tag will be ceter-aligned based on this coordinates
  x = 0,
  y = 0,
  xBuffer = 0,
  yBuffer = 0,

  id = '',
  label = '',
  selected = false,
  isTransforming = false,
  style = BOX_LABEL_DEFAULT,

  onClick = () => {},
  onChange = () => {},
  onTooltip = () => {},
  onDelete = () => {},
  onContextMenu = () => {},
}: TagProps & LabelProps & LabelPropsEvent) {
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [isHover, setIsHover] = useState(false);

  const groupRef = useRef<Konva.Group>(null);

  useEffect(() => {
    // Calculate width based on label/text width
    const textWidth = CHAR_WIDTH * label.length;
    const labelWidth = textWidth + DELETE_ICON_WIDTH + TAG_WIDTH + SPACER + xBuffer;

    setWidth(labelWidth);
    setHeight(LABEL_HEIGHT + yBuffer);
  }, [label, xBuffer, yBuffer]);

  useEffect(() => {
    if (!selected) {
      const {x = 0, y = 0} = groupRef?.current?.attrs;

      onChange({id, x, y});
    }
  }, [selected, onChange, id]);

  const onMouse = useCallback((isHovering: boolean) => {
    setIsHover(isHovering);
  }, []);

  const onMouseEnter = useCallback(
    (event: KonvaEventObject<MouseEvent>) => {
      const group = groupRef.current;

      onTooltip({
        show: true,
        refObj: {
          x: group?.x() || 0,
          y: event.evt.offsetY,
          width,
          height: LABEL_HEIGHT,
        },
        title: label || '',
      });
      onMouse(true);
    },
    [label, width, onTooltip, onMouse]
  );

  const onMouseLeave = useCallback(
    (event: KonvaEventObject<MouseEvent>) => {
      onTooltip({title: ''});
      onMouse(false);
    },
    [onTooltip, onMouse]
  );

  const onLocalContextMenu = useCallback(
    (event: KonvaPointerEvent) => {
      event.evt.preventDefault();

      if (onContextMenu) {
        onContextMenu(event);
      }
    },
    [onContextMenu]
  );

  return (
    <Group
      id={`${id}-tag`}
      ref={groupRef}
      name={LABEL_NAME}
      x={x - width / 2}
      y={y - height / 2}
      onClick={onClick}
      onContextMenu={onLocalContextMenu}
    >
      {!!label.length && (
        <>
          <Rect
            width={width}
            height={LABEL_HEIGHT}
            y={height - LABEL_HEIGHT}
            x={1}
            fill={selected || isHover ? SELECTED_FILL : style?.stroke}
            opacity={0.75}
          />
          {/* Label  */}
          <Text
            text={label}
            x={TAG_WIDTH}
            height={LABEL_HEIGHT}
            y={height - LABEL_HEIGHT}
            width={width - TAG_WIDTH}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            {...TEXT_LABEL}
            fill={selected || isHover ? TEXT_SELECTED_COLOR : TEXT_COLOR}
          />
          {/* Tag Icon */}
          <Path
            {...TAG_ICON}
            y={height - LABEL_HEIGHT + 3}
            fill={selected || isHover ? TAG_SELECTED_FILL : TAG_FILL}
          />
        </>
      )}
      {/* Delete Icon */}
      {selected && !isTransforming && (
        <Path
          {...DELETE_ICON}
          x={TAG_WIDTH}
          y={TAG_WIDTH}
          onClick={() => {
            onDelete(id);
          }}
        />
      )}
    </Group>
  );
}

export default Tag;
